import { memo, useCallback, useMemo, useState } from "react";
import {
  handlePluralString,
  hasSubscription,
  isDefaultTemplateAutomations,
  MARKIT_DOCS,
  MARKIT_TEXTING_CREDITS,
  mostRecentSubscription,
  NON_SUBSCRIBER_REPLY_CAMPAIGN_ID,
  userPhoneVerificationStatus,
} from "@markit/common.utils";
import PopupModalContainer from "../../../Containers/PopupModalContainer";
import { Icon } from "@iconify/react";
import { CircularProgress } from "@mui/material";
import RectangleButton from "../../../Buttons/RectangleButton";
import { Colors } from "../../../../utils/colors";
import TextCreditsNotice from "./TextCreditsNotice";
import { useSelector } from "react-redux";
import { getAccountState } from "../../../../redux/slices/accountSlice";
import {
  MassText,
  TextComposerAssistType,
  TwilioPhoneVerificationStatus,
} from "@markit/common.types";
import TextHoverButton from "../../../Buttons/TextHoverButton";
import { NavigationId } from "../../../../navigation/AppParamList";
import { useNavigate } from "react-router-dom";
import ConfirmActionModal from "../../../Containers/ConfirmPopups/ConfirmActionModal";
import { API } from "../../../../API";
import {
  shouldCheckTextComposerRecommendation,
  TextComposerLoadingStatus,
} from "../../../../utils/textComposerUtils";
import useAsyncOnMount from "../../../../hooks/useAsyncEffectOnMount";
import DropdownArrowIcon from "../../../DropdownsAndTabs/DropdownArrowIcon";
import {
  TextComposerSuggestion,
  TextComposerSuggestionResult,
} from "../TextComposer/TextComposerSuggestion";

type ConfirmSendTextModalProps = {
  setModalVisible: (modalVisible: boolean) => void;
  confirmOnPress: () => void;
  isTriggerText: boolean;
  isScheduled: boolean;
  numCredits: number;
  numCreditsOppositeCompliance: number;
  numFollowersNotCompliant: number;
  showComplianceCreditNotice: boolean;
  massText: MassText;
  updateMassTextSettings: (massText: Partial<MassText>) => void;
  recipients: string[];
  isSending: boolean;
  showCategories: boolean;
  sentRecentText: boolean;
};

export const ConfirmSendTextModal = memo(function ConfirmSendTextModalFn(
  props: ConfirmSendTextModalProps
) {
  const {
    setModalVisible,
    confirmOnPress,
    isTriggerText,
    isScheduled,
    numCredits,
    numCreditsOppositeCompliance,
    numFollowersNotCompliant,
    showComplianceCreditNotice,
    massText,
    updateMassTextSettings,
    recipients,
    isSending,
    showCategories,
    sentRecentText,
  } = props;
  const { accountData } = useSelector(getAccountState).account;
  const navigate = useNavigate();
  const [confirmViewStatus, setConfirmViewStatus] = useState(false);
  const [recommendationStatus, setRecommendationStatus] =
    useState<TextComposerLoadingStatus>(TextComposerLoadingStatus.IDLE);
  const [recommendation, setRecommendation] = useState<{
    assistType: TextComposerAssistType;
    reason: string;
  } | null>(null);
  const [isRecommendationExpanded, setIsRecommendationExpanded] =
    useState(false);
  const [rewriteStatus, setRewriteStatus] = useState<TextComposerLoadingStatus>(
    TextComposerLoadingStatus.IDLE
  );
  const [rewriteSuggestion, setRewriteSuggestion] =
    useState<TextComposerSuggestionResult | null>(null);

  // Gated so this only fires when the draft actually looks like it could
  // benefit - runs once when the modal opens, not on every keystroke. The
  // banner shows a loading state as soon as the gate passes so there's
  // immediate feedback that something is happening, not just a delayed pop-in.
  useAsyncOnMount(async () => {
    if (!shouldCheckTextComposerRecommendation(massText.message, numCredits)) {
      return;
    }
    setRecommendationStatus(TextComposerLoadingStatus.LOADING);
    try {
      const response = await API.text.textComposerSuggestion({
        draftText: massText.message,
        eventRefId: massText.eventRefId,
      });
      if (response.success && response.recommendedAssist) {
        setRecommendation({
          assistType: response.recommendedAssist,
          reason: response.reason ?? "",
        });
        setRecommendationStatus(TextComposerLoadingStatus.READY);
      } else {
        setRecommendationStatus(TextComposerLoadingStatus.NONE);
      }
    } catch (error) {
      console.error("Error getting text composer recommendation:", error);
      setRecommendationStatus(TextComposerLoadingStatus.ERROR);
    }
  });

  // Runs the actual rewrite only the first time the fold opens; toggling
  // closed and back open again just reveals the cached result.
  const onToggleRecommendationFold = useCallback(() => {
    if (!recommendation) {
      return;
    }
    const nextExpanded = !isRecommendationExpanded;
    setIsRecommendationExpanded(nextExpanded);
    if (!nextExpanded || rewriteStatus !== TextComposerLoadingStatus.IDLE) {
      return;
    }

    setRewriteStatus(TextComposerLoadingStatus.LOADING);
    API.text
      .textComposer({
        draftText: massText.message,
        assistType: recommendation.assistType,
        eventRefId: massText.eventRefId,
      })
      .then((response) => {
        if (response.success && response.rewrittenText) {
          setRewriteSuggestion({ rewrittenText: response.rewrittenText });
          setRewriteStatus(TextComposerLoadingStatus.READY);
        } else {
          setRewriteStatus(TextComposerLoadingStatus.NONE);
        }
      })
      .catch((error) => {
        console.error("Error getting text composer rewrite:", error);
        setRewriteStatus(TextComposerLoadingStatus.ERROR);
      });
  }, [
    recommendation,
    isRecommendationExpanded,
    rewriteStatus,
    massText.message,
    massText.eventRefId,
  ]);

  const onUseRecommendedRewrite = useCallback(() => {
    if (rewriteSuggestion) {
      updateMassTextSettings({ message: rewriteSuggestion.rewrittenText });
    }
    setModalVisible(false);
  }, [rewriteSuggestion, updateMassTextSettings, setModalVisible]);

  const styles = {
    mediaNoticeContainer: {
      borderRadius: 12,
      padding: 14,
      backgroundColor: Colors.ORANGE1 + "1a",
      border: `1px solid ${Colors.ORANGE1}`,
      gap: 7,
    },
  };

  const accountStatus = useMemo(
    () => userPhoneVerificationStatus(accountData),
    [accountData]
  );

  const showCategoryView = useMemo(
    () => showCategories && isScheduled,
    [isScheduled, showCategories]
  );

  const isTriggerOrTemplateText = useMemo(
    () => isTriggerText || isDefaultTemplateAutomations(massText.campaignId),
    [massText.campaignId, isTriggerText]
  );

  const numCompliantRecipients = useMemo(
    () => recipients.length - numFollowersNotCompliant,
    [recipients.length, numFollowersNotCompliant]
  );

  const numCreditsWithoutCompliance = useMemo(
    () =>
      showComplianceCreditNotice ? numCreditsOppositeCompliance : numCredits,
    [showComplianceCreditNotice, numCredits, numCreditsOppositeCompliance]
  );

  const numCreditsWithCompliance = useMemo(
    () =>
      showComplianceCreditNotice ? numCredits : numCreditsOppositeCompliance,
    [showComplianceCreditNotice, numCredits, numCreditsOppositeCompliance]
  );

  // The total number of credits factors in the compliance credits if the user is sending to non-compliance recipients or it's the clicked subscribe text
  const totalNumCredits = useMemo(
    () =>
      isTriggerOrTemplateText ||
      massText.campaignId === NON_SUBSCRIBER_REPLY_CAMPAIGN_ID ||
      showCategoryView
        ? numCredits
        : numFollowersNotCompliant > 0 &&
            numCreditsWithCompliance !== numCreditsWithoutCompliance
          ? numCreditsWithoutCompliance * numCompliantRecipients +
            numCreditsWithCompliance * numFollowersNotCompliant
          : numCredits * recipients.length,
    [
      isTriggerOrTemplateText,
      massText.campaignId,
      showCategoryView,
      numCredits,
      numFollowersNotCompliant,
      numCreditsWithCompliance,
      numCreditsWithoutCompliance,
      numCompliantRecipients,
      recipients.length,
    ]
  );

  const mostRecentSub = useMemo(() => {
    return mostRecentSubscription(accountData);
  }, [accountData]);

  const mostRecentLimit = useMemo(() => {
    if (mostRecentSub) {
      return mostRecentSub.price.find((p) => p.type === "baseline")?.limit || 0;
    }
    return 0;
  }, [mostRecentSub]);

  // Check if sending total num credits for trial subscription will exceed limit
  const exceedsTrialMessageLimit = useMemo(
    () =>
      mostRecentSub &&
      accountData.customer.state === "TRIAL" &&
      mostRecentLimit &&
      mostRecentSub.message + totalNumCredits > mostRecentLimit,
    [
      accountData.customer.state,
      mostRecentLimit,
      mostRecentSub,
      totalNumCredits,
    ]
  );

  const textingCreditsOnPress = useCallback(() => {
    window.open(MARKIT_TEXTING_CREDITS);
  }, []);

  return (
    <>
      <PopupModalContainer
        headerComp={
          <div className="ColumnNormal" style={{ gap: 14, fontWeight: 400 }}>
            {!isTriggerText ? (
              <Icon
                icon={
                  (!isTriggerText && isScheduled) || isTriggerOrTemplateText
                    ? "ion:timer"
                    : "ion:paper-plane"
                }
                height={35}
              />
            ) : null}
            <div className="ColumnNormal" style={{ gap: 7 }}>
              <span className="sectionTitle">
                {exceedsTrialMessageLimit
                  ? "This text will exceed your free trial credits"
                  : isTriggerOrTemplateText
                    ? "Save Text?"
                    : isScheduled
                      ? "Ready to Schedule?"
                      : "Ready to Send?"}
              </span>
              {exceedsTrialMessageLimit ? (
                <>
                  <span className="smallBodySubtext">
                    You will immediately be charged for the price of your
                    subscription plan with the card on file.
                  </span>
                  {mostRecentSub ? (
                    <span className="smallBodySubtext">
                      Your current trial text credit usage is{" "}
                      {mostRecentSub.message}/{mostRecentLimit}
                    </span>
                  ) : null}
                </>
              ) : recipients.length > 0 && !showCategoryView ? (
                <span className="smallBodySubtext">
                  This text will send to {recipients.length}{" "}
                  {handlePluralString("recipient", recipients.length)}
                  {massText.mediaUrl ? " with media pricing applied" : ""}. This
                  cannot be undone.
                </span>
              ) : null}
            </div>
            <TextCreditsNotice numCredits={numCredits} />
            {/* At most one notice banner - recent-send heads up takes priority over the Verizon media notice. */}
            {sentRecentText ? (
              <div className="ColumnNormal" style={styles.mediaNoticeContainer}>
                <span className="bodyMedium">
                  Heads Up - You Sent a Text Recently
                </span>
                <span className="bodySmallRegular">
                  You sent a text within the last 15 minutes. Check your recent
                  mass texts to confirm this isn&apos;t a duplicate before
                  sending.
                </span>
              </div>
            ) : massText.mediaUrl ? (
              <div className="ColumnNormal" style={styles.mediaNoticeContainer}>
                <span className="bodyMedium">
                  Verizon Media Delivery Notice
                </span>
                <span className="bodySmallRegular">
                  Due to a Verizon-specific carrier issue (impacting 23% of
                  phones in the US), images and contact cards may be delivered
                  in a separate text from your number. This should be noted when
                  sending texts with media.
                </span>
                <TextHoverButton
                  text="Contact Support with Questions"
                  onPress={() => window.open(MARKIT_DOCS + "/contactsupport")}
                  icon={
                    <Icon
                      icon="ion:chevron-forward"
                      height={14}
                      style={{ color: Colors.ORANGE1 }}
                    />
                  }
                  iconPosition="right"
                  textStyles={{ color: Colors.ORANGE1, fontWeight: 500 }}
                />
              </div>
            ) : null}
            <div className="ColumnNormal" style={{ gap: 4 }}>
              <div className="AlignedRow" style={{ gap: 7 }}>
                <Icon icon={"ion:prism"} height={15} />
                <span className="bodyMedium">
                  {showCategoryView || isTriggerOrTemplateText ? (
                    <span>
                      This text will use{" "}
                      <span style={{ color: Colors.BLUE5 }}>
                        {totalNumCredits}{" "}
                        {handlePluralString("credit", totalNumCredits)}
                      </span>{" "}
                      per recipient
                    </span>
                  ) : (
                    <span>
                      Sending this text will use{" "}
                      <span style={{ color: Colors.BLUE5 }}>
                        {totalNumCredits}{" "}
                        {handlePluralString("credit", totalNumCredits)}
                      </span>
                    </span>
                  )}
                </span>
              </div>
              {isTriggerText ? (
                <span className="smallBodySubtext">
                  The final credit usage of this text will depend on the total
                  number of recipients.
                </span>
              ) : showCategoryView ? (
                <span className="smallBodySubtext">
                  The total credit number may fluctuate based on the total
                  number of recipients at time of send.
                </span>
              ) : (
                <>
                  {numCompliantRecipients ? (
                    <span className="smallBodySubtext">
                      {numCreditsWithoutCompliance}{" "}
                      {handlePluralString(
                        "credit",
                        numCreditsWithoutCompliance
                      )}{" "}
                      per text for {numCompliantRecipients}{" "}
                      {handlePluralString("recipient", numCompliantRecipients)}
                    </span>
                  ) : null}
                  {numFollowersNotCompliant > 0 ? (
                    <span className="smallBodySubtext">
                      {numCreditsWithCompliance}{" "}
                      {handlePluralString("credit", numCreditsWithCompliance)}{" "}
                      per text for {numFollowersNotCompliant}{" "}
                      {handlePluralString(
                        "recipient",
                        numFollowersNotCompliant
                      )}{" "}
                      that require compliance message
                    </span>
                  ) : null}
                </>
              )}
              <TextHoverButton
                text="Learn more about credit usage"
                onPress={textingCreditsOnPress}
                icon={
                  <Icon
                    icon="ion:chevron-forward"
                    height={12}
                    style={{ color: Colors.GRAY1 }}
                  />
                }
                iconPosition="right"
              />
            </div>
            {recommendationStatus === TextComposerLoadingStatus.LOADING ||
            recommendationStatus === TextComposerLoadingStatus.READY ? (
              <div
                className="ColumnNormal"
                style={{
                  borderRadius: 14,
                  padding: 14,
                  border: `1px solid ${Colors.PINK2}`,
                  gap: 7,
                }}
              >
                <div
                  className="AlignedRowSpaced"
                  style={{ cursor: recommendation ? "pointer" : "default" }}
                  onClick={
                    recommendation ? onToggleRecommendationFold : undefined
                  }
                >
                  <div className="AlignedRow" style={{ gap: 7 }}>
                    <Icon
                      icon="ion:sparkles"
                      height={14}
                      style={{ color: Colors.PINK2 }}
                    />
                    <span
                      className="bodyMedium"
                      style={{ color: Colors.PINK2 }}
                    >
                      AI Suggestion
                    </span>
                  </div>
                  {recommendationStatus ===
                  TextComposerLoadingStatus.LOADING ? (
                    <CircularProgress
                      size={13}
                      style={{ color: Colors.GRAY1 }}
                    />
                  ) : (
                    <DropdownArrowIcon
                      isOpen={isRecommendationExpanded}
                      color={Colors.GRAY1}
                      height={14}
                    />
                  )}
                </div>
                {recommendationStatus === TextComposerLoadingStatus.READY &&
                recommendation ? (
                  <span className="smallBodySubtext">
                    {recommendation.reason}
                  </span>
                ) : null}
                {isRecommendationExpanded ? (
                  <div style={{ paddingTop: 10 }}>
                    {rewriteStatus === TextComposerLoadingStatus.LOADING ? (
                      <div className="AlignedRow" style={{ gap: 7 }}>
                        <CircularProgress
                          size={14}
                          style={{ color: Colors.PINK2 }}
                        />
                        <span className="smallBodySubtext">Rewriting...</span>
                      </div>
                    ) : rewriteStatus === TextComposerLoadingStatus.READY &&
                      rewriteSuggestion ? (
                      <TextComposerSuggestion
                        suggestion={rewriteSuggestion}
                        onUseThis={onUseRecommendedRewrite}
                        onKeepMine={() => setIsRecommendationExpanded(false)}
                      />
                    ) : rewriteStatus === TextComposerLoadingStatus.ERROR ? (
                      <span
                        className="smallBodySubtext"
                        style={{ color: Colors.RED3 }}
                      >
                        Couldn&apos;t generate a suggestion right now - try
                        again in a moment.
                      </span>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ) : null}
            {hasSubscription(accountData) &&
            accountStatus !== TwilioPhoneVerificationStatus.APPROVED ? (
              <div className="ColumnNormal" style={{ gap: 4 }}>
                <div className="AlignedRow" style={{ gap: 7 }}>
                  <Icon
                    icon="ion:warning"
                    height={15}
                    style={{
                      color:
                        accountStatus === TwilioPhoneVerificationStatus.REJECTED
                          ? Colors.RED3
                          : Colors.ORANGE1,
                    }}
                  />
                  <span
                    className="bodyMedium"
                    style={{
                      color:
                        accountStatus === TwilioPhoneVerificationStatus.REJECTED
                          ? Colors.RED3
                          : Colors.ORANGE1,
                    }}
                  >
                    Warning Before You Send
                  </span>
                </div>
                <span className="smallBodySubtext">
                  Your account{" "}
                  {accountStatus === TwilioPhoneVerificationStatus.REJECTED
                    ? "was rejected"
                    : "is still under review"}
                  . This text will be sent from a different phone number until{" "}
                  {accountStatus === TwilioPhoneVerificationStatus.REJECTED
                    ? "you contact support to resubmit"
                    : "your personal Markit number is approved"}
                  .
                </span>
                <TextHoverButton
                  text="View account status"
                  onPress={() => setConfirmViewStatus(true)}
                  icon={
                    <Icon
                      icon="ion:chevron-forward"
                      height={12}
                      style={{ color: Colors.GRAY1 }}
                    />
                  }
                  iconPosition="right"
                />
              </div>
            ) : null}
          </div>
        }
        valueComp={
          <div className="AlignedRow" style={{ gap: 10 }}>
            <RectangleButton
              buttonLabel="Cancel"
              onPress={() => setModalVisible(false)}
              altTextColor={Colors.BLACK}
              altColor={Colors.WHITE1}
              altPaddingVert={14}
              containerStyles={{ border: `0.5px solid ${Colors.GRAY2}` }}
            />
            <RectangleButton
              buttonLabel={
                isTriggerOrTemplateText
                  ? "Save"
                  : isScheduled
                    ? "Schedule"
                    : "Send Text"
              }
              onPress={confirmOnPress}
              altTextColor={Colors.WHITE}
              altColor={Colors.BLACK}
              altPaddingVert={14}
              loading={isSending}
            />
          </div>
        }
        noExit
        closeOnOutsidePress={!confirmViewStatus}
        closeModal={() => setModalVisible(false)}
      />
      <ConfirmActionModal
        heading={"This will redirect you to view your account status."}
        subtext={"Any progress on this text will be lost. Continue?"}
        confirmButtonText={"Continue"}
        hideModal={!confirmViewStatus}
        setIsVisible={() => setConfirmViewStatus(!confirmViewStatus)}
        confirmOnPress={() => navigate(NavigationId.HOME_ACCOUNT_STATUS)}
      />
    </>
  );
});
